import { defineTask, type AnyTask, type TaskArgs, type TaskResult } from "./src/task"

export { defineTask }
export type { AnyTask, Task, TaskArgs, TaskContext, TaskOutcome, TaskResult } from "./src/task"

export type RunTaskOptions = { signal?: AbortSignal; transfer?: ArrayBuffer[] }

export type BrowserWorkerHandle = {
    postMessage(message: unknown, transfer: ArrayBuffer[]): void
    addEventListener(type: "message", listener: (event: { data: unknown }) => void): void
    removeEventListener(type: "message", listener: (event: { data: unknown }) => void): void
    terminate(): void
}

export type NodeWorkerHandle = {
    postMessage(value: unknown, transferList?: readonly ArrayBuffer[]): void
    on(event: "message", listener: (value: unknown) => void): unknown
    off(event: "message", listener: (value: unknown) => void): unknown
    terminate(): Promise<number>
}

export type PoolContract = {
    defineTask: typeof defineTask
    runTask: <T extends AnyTask>(
        task: T,
        args: TaskArgs<T>,
        options?: RunTaskOptions
    ) => Promise<TaskResult<T>>
    getNumberOfWorkers: () => number
    cleanupWorkers: () => void
}

export const wasmBinaries = {
    clipper: "clipper2z.wasm",
    sensortool: "sensortool.wasm",
} as const

export type WasmBinaryName = keyof typeof wasmBinaries

export type WorkerInbound =
    | {
          type: "run"
          taskId: string
          taskName: string
          args: unknown
          wasm: { name: WasmBinaryName; module: WebAssembly.Module }[]
      }
    | { type: "abort"; taskId: string }

export type WorkerOutbound =
    | { type: "done"; taskId: string; data: unknown }
    | { type: "failed"; taskId: string; error: string }

export type Conforms<Facade extends PoolContract> = Facade

export declare const runTask: PoolContract["runTask"]
export declare const getNumberOfWorkers: PoolContract["getNumberOfWorkers"]
export declare const cleanupWorkers: PoolContract["cleanupWorkers"]
